import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  GraduationCap,
  Heart,
  ShoppingCart,
  Factory,
  Building,
  DollarSign,
  Truck,
  Rocket,
  ArrowRight
} from 'lucide-react';

const Industries = () => {
  const industries = [
    {
      icon: GraduationCap,
      name: "Education",
      description: "School management systems, learning platforms, student tracking, and educational ERP solutions",
      services: ["School ERP", "Learning Management", "Student Portal", "Fee Management"]
    },
    {
      icon: Heart,
      name: "Healthcare",
      description: "Hospital management systems, patient records, appointment scheduling, and medical billing",
      services: ["Hospital ERP", "Patient Management", "Appointment System", "Medical Billing"]
    },
    {
      icon: ShoppingCart,
      name: "Retail & E-commerce",
      description: "POS systems, inventory management, customer relationship management, and online stores",
      services: ["POS Solutions", "Inventory Management", "E-commerce Platform", "CRM"]
    },
    {
      icon: Factory,
      name: "Manufacturing",
      description: "Production planning, inventory control, quality management, and supply chain solutions",
      services: ["Manufacturing ERP", "Quality Control", "Supply Chain", "Warehouse Management"]
    },
    {
      icon: Building,
      name: "Real Estate",
      description: "Property management, customer relationship management, and real estate CRM solutions",
      services: ["Property Management", "Lead Management", "Customer Portal", "Document Management"]
    },
    {
      icon: DollarSign,
      name: "Finance & Banking",
      description: "Financial management systems, accounting software, and secure payment solutions",
      services: ["Accounting Software", "Financial ERP", "Payment Gateway", "Compliance Management"]
    },
    {
      icon: Truck,
      name: "Logistics & Transportation",
      description: "Fleet management, route optimization, shipment tracking, and warehouse management",
      services: ["Fleet Management", "Tracking System", "Route Optimization", "Warehouse ERP"]
    },
    {
      icon: Rocket,
      name: "Startups & Technology",
      description: "Custom software development, MVP development, SaaS platforms, and tech consulting",
      services: ["MVP Development", "SaaS Platform", "Mobile Apps", "Tech Consulting"]
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-white to-cyan-50">
        <div className="max-w-7xl mx-auto text-center">
          <Badge className="bg-blue-100 text-blue-700 mb-4">Industries We Serve</Badge>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6 leading-tight">
            Serving{' '}
            <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">
              Diverse Industries
            </span>
            {' '}with Excellence
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8 leading-relaxed">
            Our technology and workforce solutions are tailored to meet the unique 
            challenges and requirements of various industry sectors.
          </p>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {industries.map((industry, index) => (
              <Card 
                key={index} 
                className="p-8 hover:shadow-2xl transition-all duration-300 border-2 hover:border-blue-200 group"
              >
                <div className="flex items-start space-x-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-cyan-500 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                    <industry.icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{industry.name}</h3>
                    <p className="text-gray-600 mb-6 leading-relaxed">{industry.description}</p>
                    <div>
                      <div className="text-sm font-semibold text-gray-700 mb-3">Our Solutions:</div>
                      <div className="flex flex-wrap gap-2">
                        {industry.services.map((service, i) => (
                          <Badge 
                            key={i} 
                            className="bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200"
                          >
                            {service}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us for Your Industry */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Industry-Specific Expertise
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We understand the unique challenges of each industry and provide tailored solutions
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="p-8 text-center bg-white border-2 hover:border-blue-200 transition-all duration-300">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Rocket className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Domain Knowledge</h3>
              <p className="text-gray-600">
                Deep understanding of industry-specific workflows, regulations, and best practices
              </p>
            </Card>

            <Card className="p-8 text-center bg-white border-2 hover:border-blue-200 transition-all duration-300">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Building className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Proven Track Record</h3>
              <p className="text-gray-600">
                Successfully delivered projects across multiple industries with high satisfaction
              </p>
            </Card>

            <Card className="p-8 text-center bg-white border-2 hover:border-blue-200 transition-all duration-300">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <GraduationCap className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Customized Solutions</h3>
              <p className="text-gray-600">
                Tailored technology and workforce solutions designed for your specific needs
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Industry Stats */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="bg-gradient-to-r from-blue-600 to-cyan-500 rounded-2xl p-12 text-white">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Our Impact Across Industries</h2>
              <p className="text-xl text-blue-100">
                Numbers that speak for our industry expertise
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="text-4xl sm:text-5xl font-bold mb-2">8+</div>
                <div className="text-blue-100">Industries Served</div>
              </div>
              <div className="text-center">
                <div className="text-4xl sm:text-5xl font-bold mb-2">500+</div>
                <div className="text-blue-100">Clients Served</div>
              </div>
              <div className="text-center">
                <div className="text-4xl sm:text-5xl font-bold mb-2">1000+</div>
                <div className="text-blue-100">Projects Delivered</div>
              </div>
              <div className="text-center">
                <div className="text-4xl sm:text-5xl font-bold mb-2">95%</div>
                <div className="text-blue-100">Satisfaction Rate</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
            Ready to Transform Your Industry Operations?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Let's discuss how our solutions can benefit your specific industry
          </p>
          <Link to="/contact">
            <Button className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white px-8 py-6 text-lg rounded-lg">
              Get Industry-Specific Consultation
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Industries;
